import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Copy, ArrowRight, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", token: null, email: null });

  useEffect(() => {
    if (!sessionId) return setState({ status: "error" });
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await http.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" && data.access_token) {
          localStorage.setItem(`wl-access-${sessionId}`, data.access_token);
          return setState({ status: "paid", token: data.access_token, email: data.email });
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState({ status: "error" });
      } catch { /* retry */ }
      if (++tries >= 15) return setState({ status: "timeout" });
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const link = state.token ? `${window.location.origin}/premium/${state.token}` : "";
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Lien copié"); };

  return (
    <div className="px-4 max-w-2xl mx-auto py-24">
      {state.status === "checking" && (
        <div data-testid="payment-checking" className="text-center"><Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto" /><p className="mt-4 text-gray-600">Confirmation de votre paiement…</p></div>
      )}
      {state.status === "paid" && (
        <div data-testid="payment-success" className="rise rounded-2xl bg-white border border-gray-200 p-8 sm:p-10">
          <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl font-bold text-[#0B131E]">Paiement confirmé</h1>
          <p className="mt-3 text-gray-600">Votre itinéraire Premium est débloqué. Conservez ce lien unique : il vous donne accès à votre escapade à tout moment, sans compte.</p>
          {state.email ? (
            <p data-testid="premium-email-notice" className="mt-4 flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 rounded-xl px-4 py-3">
              <Mail className="w-4 h-4 shrink-0" />Ce lien vous est aussi envoyé par email à {state.email}, avec un aperçu de votre programme (sous 1 à 2 minutes).
            </p>
          ) : null}
          <div className="mt-6 flex gap-2">
            <input readOnly value={link} data-testid="premium-access-link" className="flex-1 h-11 px-3 rounded-xl border border-gray-200 bg-gray-50 text-sm font-mono text-gray-700" />
            <button onClick={copy} data-testid="copy-access-link-btn" className="h-11 px-4 rounded-xl border border-gray-200 hover:border-amber-600 transition-colors"><Copy className="w-4 h-4" /></button>
          </div>
          <Link to={`/premium/${state.token}`} data-testid="open-premium-btn" className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-colors">
            Ouvrir mon itinéraire <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
      {(state.status === "error" || state.status === "timeout") && (
        <div data-testid="payment-error" className="text-center">
          <AlertCircle className="w-8 h-8 text-amber-600 mx-auto" />
          <p className="mt-4 text-gray-700">{state.status === "timeout" ? "La confirmation prend plus de temps que prévu. Rechargez la page dans un instant." : "Le paiement n'a pas pu être confirmé."}</p>
          <Link to="/" className="mt-4 inline-block text-amber-700 underline">Retour à l'accueil</Link>
        </div>
      )}
    </div>
  );
}
